import React from 'react'
import moment from 'moment'
import styled from 'styled-components'
import {Link} from 'react-router-dom'
import {injectIntl} from 'react-intl'
import FormControl from '@mui/material/FormControl'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import CellValue from 'components/CellValue'
import {fieldtype} from 'utils/enums/fieldtype'
import {icons} from 'utils/enums/icons'

const Value = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    height: 45px;
    line-height: 45px;
`

const Label = styled.div`
    font-weight: 600;
    margin: 10px 10px 0 10px;
    display: flex;
    align-items: center;
`

const Icon = styled.i`
    color: #a0b7be;
    font-size: 24px;
    padding-right: 10px;
`

const Field = styled.div`
    border-top-right-radius: 2px;
    border-top-left-radius: 2px;
    background-color: rgba(160, 183, 190, 0.24);
    margin-bottom: 20px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
`

const Ref = styled.div`
    display: flex;
    margin-top: 20px;

    > a {
        color: #4a4a4a;
        font-size: 15px;
        border-bottom: 1px solid #4a4a4a;
        margin-left: auto;
    }
`

class EventConfig extends React.Component {
    constructor(props) {
        super(props)

        this.state = {
            entityId: props.config ? props.config.entityId : props.entities[0].id,
            data: props.config ? {...props.config.data} : this.getData(props.entities[0].id)
        }
    }

    getData = (entityId) => {
        const entity = this.props.entities.find(item => item.id === entityId)
        const dataset = this.props.planning.datasets.find(dataset => dataset.entityId === entityId)
        const fromField = entity.fields.find(field => field.id === dataset.start)
        const toField = entity.fields.find(field => field.id === dataset.end)
        const ownerField = entity.fields.find(field => field.id === dataset.owner)
        const consumerField = entity.fields.find(field => field.id === dataset.consumer)
        
        const date = new Date(this.props.date)
        date.setMinutes(date.getMinutes() + dataset.defaultDuration)
        const value = date.toISOString()

        const data = {
            ['c' + fromField.id]: this.props.date,
            ['c' + toField.id]: value,
            ['c' + ownerField.linkedRelation[0]]: [],
            ['c' + consumerField.linkedRelation[0]]: []
        }

        return data
    }

    handleChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value,
            data: this.props.config && this.props.config.entityId === e.target.value ?
                {...this.props.config.data} : this.getData(e.target.value)
        })
    }

    handleUpdateValue = (value, name) => {
        const data = {...this.state.data}
        const entity = this.props.entities.find(item => item.id === this.state.entityId)
        const field = entity.fields.find(item => item.id === name)

        if (field) {
            if (field.uiType === fieldtype.linkedfield) {
                data['c' + field.linkedRelation[0]] = value
            } else {
                data['c' + name] = value
            }

            this.setState({
                data
            })
        }
    }

    updateEvent = () => {
        this.props.updateEvent(this.state.entityId, this.state.data, this.props.planning.id)
    }

    createEvent = () => {
        this.props.createEvent(this.state.entityId, this.state.data, this.props.planning.id)
    }

    render() {
        const {intl, config, entities, planning} = this.props
        const entity = entities.find(item => item.id === this.state.entityId)
        const dataset = planning.datasets.find(dataset => dataset.entityId === this.state.entityId)
        const fields = entity.fields.filter(field =>
            field.id === dataset.end || field.id === dataset.start ||
            field.id === dataset.owner || field.id === dataset.consumer)

        const owner = entity.fields.find(field => field.id === dataset.owner)
        const valueStart = this.state.data['c' + dataset.start]
        const valueEnd = this.state.data['c' + dataset.end]
        const valueOwner = this.state.data['c' + owner.linkedRelation[0]]
        const invalid = !valueOwner.length || !valueStart || !valueEnd || moment(valueStart).isAfter(moment(valueEnd))

        const data = fields.map(item => {
                return {
                    name: item.id,
                    title: item.singular,
                    defaultValue: item.uiType === fieldtype.linkedfield ?
                        this.state.data['d' + item.id] : this.state.data['c' + item.id],
                    type: item.uiType,
                    config: item.uiConfig,
                    foreignerEcn: item.foreignerEcn,
                    foreignerFcns: item.foreignerFCNs,
                    linkedRelation: item.uiType === fieldtype.linkedfield && item.linkedRelation.length ?
                        this.state.data['c' + item.linkedRelation[0]] : undefined
                }
            })

        return (
            <React.Fragment>
                <DialogTitle>
					{intl.formatMessage({id: this.props.config ? "planning.edit_event" : "planning.create_event"})}
                </DialogTitle>
                <DialogContent>
                    <React.Fragment>
                        {!config && planning.datasets.length > 1 &&
                            entities && entities.length &&
                            <FormControl variant="standard"
								sx={{
									marginBottom: '20px',
									width: '100%'
								}}
							>
                                <InputLabel>{intl.formatMessage({id: "main.planning_entity"})}</InputLabel>
                                <Select
                                    value={this.state.entityId}
                                    onChange={this.handleChange}
                                    inputProps={{
                                        name: 'entityId'
                                    }}
                                >
                                    {entities.map((item, index) =>
                                        <MenuItem key={index} value={item.id}>{item.singular}</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        }
                        <React.Fragment>
                            {data.map(item =>
                                <Field key={item.name}>
                                    <Label>
                                        <Icon className={icons[item.type.toLowerCase()]} />
                                        <span>{item.title}</span>
                                    </Label>
                                    <Value>
                                        <CellValue
                                            title={item.title}
                                            config={item.config}
                                            type={item.type}
                                            defaultValue={item.defaultValue}
                                            foreignerEcn={item.foreignerEcn}
                                            linkedRelation={item.linkedRelation}
                                            foreignerFcns={item.foreignerFcns}
                                            name={item.name}
                                            handleUpdateValue={this.handleUpdateValue}
                                        />
                                    </Value>
                                </Field>
                            )}
                        </React.Fragment>
                        {config &&
                            <Ref>
                                <Link to={`/record/${config.entityId}/${config.recordId}`}>
                                    {intl.formatMessage({id: "planning.show_all_data"})}
                                </Link>
                            </Ref>
                        }
                    </React.Fragment>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.props.handleClose} color="primary">
                        {intl.formatMessage({id: "app.confirm_cancel"})}
                    </Button>
                    {this.props.config ?
                        <Button variant="contained"
                            disabled={invalid}
                            onClick={this.updateEvent}
                            color="primary"
                        >
                            {intl.formatMessage({id: "app.confirm_save"})}
                        </Button>
                        :
                        <Button variant="contained"
                            disabled={invalid}
                            onClick={this.createEvent}
                            color="primary"
                        >
                            {intl.formatMessage({id: "app.confirm_create"})}
                        </Button>
                    }
                </DialogActions>
            </React.Fragment>
        )
    }
}

export default injectIntl(EventConfig)
